import { useQuery } from 'react-query';
import { fetchMoodChart, fetchMostLoggedMetrics } from 'pages/Dashboard/services/tracking.services';
import { trackingUrls } from 'utils/apiUrls';
import { MoodV2 } from 'pages/Dashboard/types/moods.types';
import { MoodFactorDomainsResponse, MostLoggedMetricsResponse } from 'pages/Dashboard/types/waypoints.types';
import { defaultMoodTracking } from 'pages/Dashboard/utils/trackingUtils';
import React from 'react';
import { useHttp } from 'hooks/use-fetch';
import { TimeFrameCategory } from 'pages/Dashboard/enums';

export enum MostLoggedMetrics {
  MOOD = 'MOOD',
  SITUATIONS = 'SITUATIONS',
  DOMAINS = 'DOMAINS',
}

interface UseMoodsProps {
  providerId: string | undefined;
  patientId: string | undefined;
  selectedTimeFrame: {
    [TimeFrameCategory.MOOD]?: string;
    [TimeFrameCategory.ENERGY]?: string;
    [TimeFrameCategory.MOOD_FACTORS]?: string;
  };
  moodId: string;
}

function useMoods({
  providerId,
  patientId,
  selectedTimeFrame,
  moodId,
}: UseMoodsProps) {
  const { http } = useHttp();

  const moodPleasantnessTimeFrame = selectedTimeFrame[TimeFrameCategory.MOOD];
  const moodEnergyTimeFrame = selectedTimeFrame[TimeFrameCategory.ENERGY];
  const moodFactorTimeFrame = selectedTimeFrame[TimeFrameCategory.MOOD_FACTORS];

  const fetchMoodChartPleasantness = useQuery(
    [
      `${trackingUrls.moodList.queryUrl}`,
      providerId,
      patientId,
      selectedTimeFrame[TimeFrameCategory.MOOD],
      MoodV2.MOOD_PLEASANTNESS,
      'charts',
    ],
    {
      queryFn: fetchMoodChart(http.get),
      enabled: !!providerId && !!patientId && !!moodPleasantnessTimeFrame,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  const fetchMoods = useQuery(
    [
      `${trackingUrls.moodList.queryUrl}`,
      providerId,
      patientId,
      selectedTimeFrame[TimeFrameCategory.MOOD_FACTORS],
      MoodV2.MOOD_PLEASANTNESS,
      'charts',
    ],
    {
      queryFn: fetchMoodChart(http.get),
      enabled: !!providerId && !!patientId && !!moodPleasantnessTimeFrame,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  const uniqueMoodsLogged = React.useMemo(() => {
    const uniqueDates = new Set<string>();
    (fetchMoods?.data?.moods || []).forEach((entry) => {
      uniqueDates.add(`${entry.date.day}-${entry.date.month}-${entry.date.year}`);
    });
    return uniqueDates.size;
  }, [fetchMoods]);

  const fetchMoodChartEnergy = useQuery(
    [
      `${trackingUrls.moodList.queryUrl}`,
      providerId,
      patientId,
      moodEnergyTimeFrame,
      MoodV2.MOOD_ENERGY,
      'charts',
    ],
    {
      queryFn: fetchMoodChart(http.get),
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: !!providerId && !!patientId && !!moodEnergyTimeFrame,
    },
  );

  const fetchMostLoggedMoodsResult = useQuery(
    [
      `${trackingUrls.mostLoggedMetrics.queryUrl}`,
      providerId,
      patientId,
      moodFactorTimeFrame,
      MostLoggedMetrics.MOOD,
    ],
    {
      queryFn: fetchMostLoggedMetrics(
        http.get,
      ),
      enabled: !!providerId && !!patientId && !!moodFactorTimeFrame,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  const fetchMostLoggedSituationResult = useQuery(
    [
      `${trackingUrls.mostLoggedMetrics.queryUrl}`,
      providerId,
      patientId,
      moodFactorTimeFrame,
      moodId,
      'mood',
    ],
    {
      queryFn: fetchMostLoggedMetrics(
        http.get as (url: string, params?: unknown) => Promise<unknown>,
      ),
      enabled: !!providerId && !!patientId && !!moodFactorTimeFrame,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  const mostLoggedMetrics = React.useMemo(
    () => ({
      mood: (fetchMostLoggedMoodsResult?.data as MostLoggedMetricsResponse)?.percents || [],
      factors:
        (fetchMostLoggedSituationResult?.data as MoodFactorDomainsResponse)
          ?.moodFactorDomains || [],
      moodsLoading: fetchMostLoggedMoodsResult?.isLoading,
      factorsLoading: fetchMostLoggedSituationResult?.isLoading,
    }),
    [
      fetchMostLoggedMoodsResult?.data,
      fetchMostLoggedMoodsResult?.isLoading,
      fetchMostLoggedSituationResult?.isLoading,
      fetchMostLoggedSituationResult?.data,
    ],
  );

  const hasMostLoggedMetrics = React.useMemo(
    () => (mostLoggedMetrics.mood.length > 0 || mostLoggedMetrics.factors.length > 0)
    || (mostLoggedMetrics.moodsLoading || mostLoggedMetrics.factorsLoading),
    [
      mostLoggedMetrics.factors.length,
      mostLoggedMetrics.factorsLoading,
      mostLoggedMetrics.mood.length,
      mostLoggedMetrics.moodsLoading,
    ],
  );

  const moodChartPleasantness = fetchMoodChartPleasantness?.data || defaultMoodTracking;
  const moodChartEnergy = fetchMoodChartEnergy?.data || defaultMoodTracking;

  return {
    mostLoggedMetrics,
    hasMostLoggedMetrics,
    moodChartEnergy,
    moodChartPleasantness,
    uniqueMoodsLogged,
    isLoading: {
      [MoodV2.MOOD_PLEASANTNESS]: fetchMoodChartPleasantness.isFetching,
      [MoodV2.MOOD_ENERGY]: fetchMoodChartEnergy.isFetching,
    },
  };
}

export default useMoods;
